import type { ReactNode } from "react";

import { useMe } from "@/hooks/useMe";
import { NoWorkForAccessPage } from "@/pages/work-for/NoWorkForAccessPage";

/**
 * Work-For surface gate (server-authoritative).
 *
 * Deliberately NOT `RequireAdminAccess`. That gate wants `canManageUsers` or
 * `canManageMasterData`, which only `admin` holds — so `sales` and
 * `engineering`, the internal roles that exist to act for a customer, were
 * sent to a dead end when customer selection moved onto this host. This gate
 * asks the one question that matters for the control it protects:
 * `canWorkForOthers` (rule §D.1).
 *
 * SECURITY: reads `role` from a FRESH `GET /v1/users/me`, never the
 * sessionStorage user cache, which is attacker-editable. The server remains
 * authoritative (§L) — every Work-For consumption point re-validates the
 * caller and target per request. This gate only decides what to render.
 *
 * Fail-closed: a neutral state while `/me` is in flight, and any error is
 * treated as no access.
 */
export const RequireWorkForAccess = ({ children }: { children: ReactNode }) => {
  const { data, isLoading, isError } = useMe();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-400">
        Checking access…
      </div>
    );
  }

  if (isError || !data?.user.role?.canWorkForOthers) {
    return <NoWorkForAccessPage />;
  }

  return <>{children}</>;
};
